import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Card, Icon, Loader, Checkbox } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { fetchAccountActivity, fetchAllAuditTrail } from './api';
import { AccountActivityElementModel, AccountActivityRequest } from './types';
import { i18nKeys } from './constants';
import ActivityCard from './components/ActivityCard';
import styles from './components/ActivityCard.module.css';

interface SessionState {
  session: {
    account?: {
      id?: number;
    };
  };
}

const AuditTrail = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionAccountId = useSelector((state: SessionState) => state.session?.account?.id);

  const [activities, setActivities] = useState<AccountActivityElementModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read filter state from URL
  const changesAndExportsOnly = searchParams.get('changesAndExportsOnly') === 'true';

  // Check if allRecords mode is enabled
  const allRecords = searchParams.get('allRecords') === 'true';

  // Build request params from URL or session
  const buildRequestParams = useCallback((): AccountActivityRequest => {
    const params: AccountActivityRequest = {
      changesAndExportsOnly: changesAndExportsOnly || undefined,
    };

    // If allRecords mode, don't add any filters
    if (allRecords) {
      return params;
    }

    // Check for query string parameters (only one filter plus page is allowed)
    const organizationId = searchParams.get('organizationId');
    const targetOrganizationId = searchParams.get('targetOrganizationId');
    const targetCampaignId = searchParams.get('targetCampaignId');
    const targetEventId = searchParams.get('targetEventId');
    const targetEventAmbassadorId = searchParams.get('targetEventAmbassadorId');
    const targetFundraiserId = searchParams.get('targetFundraiserId');
    const targetTeamId = searchParams.get('targetTeamId');
    const targetCollectionId = searchParams.get('targetCollectionId');
    const targetSaleId = searchParams.get('targetSaleId');
    const targetDonationId = searchParams.get('targetDonationId');
    const accountId = searchParams.get('accountId');

    if (organizationId) {
      params.organizationId = parseInt(organizationId, 10);
    } else if (targetOrganizationId) {
      params.targetOrganizationId = parseInt(targetOrganizationId, 10);
    } else if (targetCampaignId) {
      params.targetCampaignId = parseInt(targetCampaignId, 10);
    } else if (targetEventId) {
      params.targetEventId = parseInt(targetEventId, 10);
    } else if (targetEventAmbassadorId) {
      params.targetEventAmbassadorId = parseInt(targetEventAmbassadorId, 10);
    } else if (targetFundraiserId) {
      params.targetFundraiserId = parseInt(targetFundraiserId, 10);
    } else if (targetTeamId) {
      params.targetTeamId = parseInt(targetTeamId, 10);
    } else if (targetCollectionId) {
      params.targetCollectionId = parseInt(targetCollectionId, 10);
    } else if (targetSaleId) {
      params.targetSaleId = parseInt(targetSaleId, 10);
    } else if (targetDonationId) {
      params.targetDonationId = parseInt(targetDonationId, 10);
    } else if (accountId) {
      params.accountId = parseInt(accountId, 10);
    } else if (sessionAccountId) {
      // Default to current user's accountId
      params.accountId = sessionAccountId;
    }

    return params;
  }, [searchParams, sessionAccountId, changesAndExportsOnly]);

  // Fetch activities
  const fetchActivities = useCallback(async (pageNum: number, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      let response;
      if (allRecords) {
        // Use the dedicated endpoint for all records
        response = await fetchAllAuditTrail(changesAndExportsOnly || undefined, pageNum);
      } else {
        const params = buildRequestParams();
        params.page = pageNum;
        response = await fetchAccountActivity(params);
      }
      const { list, totalCount: total, pageSize: size } = response.data;

      if (append) {
        setActivities((prev) => [...prev, ...list]);
      } else {
        setActivities(list);
      }
      setTotalCount(total);
      setPageSize(size);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch audit trail:', err);
      setError(I18n.t(i18nKeys.ERROR_LOADING));
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [allRecords, buildRequestParams]);

  // Initial load and re-fetch when dependencies change (including filter)
  useEffect(() => {
    fetchActivities(1);
  }, [fetchActivities]);

  // Load more handler
  const handleLoadMore = () => {
    fetchActivities(page + 1, true);
  };

  // Toggle filter handler
  const handleToggleChangesAndExportsOnly = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (changesAndExportsOnly) {
        newParams.delete('changesAndExportsOnly');
      } else {
        newParams.set('changesAndExportsOnly', 'true');
      }
      return newParams;
    });
  };

  const hasMore = activities.length < totalCount;

  // Loading state
  if (isLoading) {
    return (
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Icon name="history" />
            <Translate value={i18nKeys.TITLE} />
          </Card.Header>
          <Card.Description>
            <div className={styles.loadingContainer}>
              <Loader active inline="centered" />
            </div>
          </Card.Description>
        </Card.Content>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Icon name="history" />
            <Translate value={i18nKeys.TITLE} />
          </Card.Header>
          <Card.Description>
            <div className={styles.errorState}>
              <Icon name="exclamation triangle" className={styles.errorIcon} />
              <div className={styles.errorText}>{error}</div>
            </div>
          </Card.Description>
        </Card.Content>
      </Card>
    );
  }

  // Empty state
  if (activities.length === 0) {
    return (
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Icon name="history" />
            <Translate value={i18nKeys.TITLE} />
          </Card.Header>
          <Card.Description>
            <div className={styles.filterRow}>
              <Checkbox
                label={I18n.t(i18nKeys.CHANGES_AND_EXPORTS_ONLY)}
                checked={changesAndExportsOnly}
                onChange={handleToggleChangesAndExportsOnly}
              />
            </div>
            <div className={styles.emptyState}>
              <Icon name="inbox" className={styles.emptyIcon} />
              <div className={styles.emptyText}>
                <Translate value={i18nKeys.NO_DATA} />
              </div>
            </div>
          </Card.Description>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card fluid>
      <Card.Content>
        <Card.Header>
          <Icon name="history" />
          <Translate value={i18nKeys.TITLE} />
        </Card.Header>
        <Card.Description>
          <div className={styles.filterRow}>
            <Checkbox
              label={I18n.t(i18nKeys.CHANGES_AND_EXPORTS_ONLY)}
              checked={changesAndExportsOnly}
              onChange={handleToggleChangesAndExportsOnly}
            />
          </div>
          <div className={styles.totalCount}>
            {totalCount} {totalCount === 1 ? 'record' : 'records'}
          </div>
          <div className={styles.activityList}>
            {activities.map((activity) => (
              <ActivityCard key={activity.id} activity={activity} />
            ))}
          </div>

          {hasMore && (
            <div className={styles.loadMoreContainer}>
              <button
                className={styles.loadMoreButton}
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <Loader active inline size="tiny" />
                ) : (
                  <Translate value={i18nKeys.LOAD_MORE} />
                )}
              </button>
            </div>
          )}
        </Card.Description>
      </Card.Content>
    </Card>
  );
};

export default AuditTrail;
